import React, { createContext, useContext, useState, useEffect } from 'react';
import apiClient from '../api/client';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is authenticated on app load
  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await apiClient.auth.me();
          if (response.success) {
            setUser(response.data.user);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, remove it
            localStorage.removeItem('token');
            setToken(null);
          }
        } catch (error) {
          console.error('Auth initialization failed:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    initAuth();
  }, [token]);

  const login = async (credentials) => {
    try {
      const response = await apiClient.auth.login(credentials);
      if (response.success) {
        const { token: newToken } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        
        // Get user data
        const userResponse = await apiClient.auth.me();
        if (userResponse.success) {
          setUser(userResponse.data.user);
          setIsAuthenticated(true);
        }
        
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const register = async (userData) => {
    try {
      const response = await apiClient.auth.register(userData);
      if (response.success) {
        const { token: newToken } = response.data;
        localStorage.setItem('token', newToken);
        setToken(newToken);
        
        // Get user data
        const userResponse = await apiClient.auth.me();
        if (userResponse.success) {
          setUser(userResponse.data.user);
          setIsAuthenticated(true);
        }
        
        return { success: true };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, message: 'Registration failed. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await apiClient.profile.update(profileData);
      if (response.success) {
        // Update token if new one is provided
        if (response.data.token) {
          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          setToken(newToken);
          
          // Get updated user data from the new token
          const userResponse = await apiClient.auth.me();
          if (userResponse.success) {
            setUser(userResponse.data.user);
          }
        }
        
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Profile update failed:', error);
      return { success: false, message: 'Profile update failed. Please try again.' };
    }
  };

  const deleteProfile = async () => {
    try {
      const response = await apiClient.profile.delete();
      if (response.success) {
        // Log out user after successful account deletion
        logout();
        return { success: true, message: response.message };
      } else {
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('Profile deletion failed:', error);
      return { success: false, message: 'Profile deletion failed. Please try again.' };
    }
  };

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    deleteProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
